import { gql } from "@apollo/client";
import { useCallback } from "react";
import { useMutation } from "@apollo/client/react";

import type { 
  UpdateEmail, 
  UpdateEmailResp, 
  UpdateEmailType, 
  UpdateEmailValues 
} from "../../types/updateEmailTypes";

const UPDATE_PASSWORD = gql`
  mutation UpdateEmail($attributes: UpdateEmailInput!) {
    updateEmail(attributes: $attributes) {
      message
      success
      errors
      email
    }
  }
`;

type UseUpdateEmailOptions = {
  onCompleted?: (updatedEmail: UpdateEmailType) => void;
  onError?: (error: unknown) => void;
  mutationOptions?: Parameters<typeof useMutation<UpdateEmailResp, UpdateEmailValues>>[1];
};

const useUpdateEmail = (options?: UseUpdateEmailOptions) => {
  const [mutate, { data, loading, error }] = useMutation<
    UpdateEmailResp,
    UpdateEmailValues
  >(UPDATE_PASSWORD, options?.mutationOptions);

  const updateEmail = useCallback(
    async (input: UpdateEmail) => {
      try {
        const result = await mutate({
          variables: {
            attributes: {
              currentPassword: input.currentPassword,
              email: input.email,
            },
          },
        });

        const updateEmail = result?.data?.updateEmail;
        
        if (updateEmail?.errors?.length) {
          if (updateEmail.errors.includes("current_password")) {
            throw new Error(updateEmail.message);
          }
          throw new Error(updateEmail.errors.join(", "));
        }

        if (options?.onCompleted && updateEmail) {
          options.onCompleted(updateEmail);
        }

        return updateEmail;
      } catch (err) {
        if (options?.onError) {
          options.onError(err);
        }
        throw err;
      }
    },
    [mutate, options]
  );

  const password = data?.updateEmail;

  return {
    updateEmail,
    updatedEmail: password,
    loading,
    error: error as Error | null | undefined,
  };
}

export default useUpdateEmail
